import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useStuckOrders, useProcessPendingInvoices } from "@/api/invoices";
import { AlertCircle, RefreshCw, Play } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

export function StuckOrdersTable() {
  const { data, isLoading, refetch, isRefetching } = useStuckOrders();
  const processPending = useProcessPendingInvoices();

  const handleProcessCustomer = async (customerId: string) => {
    try {
      const result = await processPending.mutateAsync({ customerId });
      if (result.invoicesGenerated > 0) {
        toast.success(`Invoice generated successfully`);
      } else {
        toast.info("No invoice generated - conditions not met");
      }
    } catch {
      toast.error("Failed to process invoice");
    }
  };

  const handleProcessAll = async () => {
    try {
      const result = await processPending.mutateAsync({});
      toast.success(
        `Processed ${result.processed} customers, generated ${result.invoicesGenerated} invoices`
      );
    } catch {
      toast.error("Failed to process pending invoices");
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-4 text-muted-foreground">Loading...</div>
    );
  }

  if (!data || data.stuckCount === 0) {
    return (
      <div className="border rounded-lg p-6 text-center text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
        <p>No stuck orders found. All invoices are up to date.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-500" />
          <span className="font-medium">
            {data.stuckCount} customer{data.stuckCount > 1 ? "s" : ""} with
            stuck orders
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isRefetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleProcessAll}
            disabled={processPending.isPending}
          >
            <Play className="h-4 w-4 mr-2" />
            Process All
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Payment Terms</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.customers.map((customer) => (
              <TableRow key={customer.customerId}>
                <TableCell>
                  <div>
                    <p className="font-medium">{customer.customerName}</p>
                    <p className="text-xs text-muted-foreground">
                      Oldest order:{" "}
                      {formatDistanceToNow(new Date(customer.oldestOrderDate), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p>{customer.pendingOrderCount} pending</p>
                    <p className="text-xs text-muted-foreground">
                      {customer.completedOrderCount} completed
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <Badge variant="outline">
                      {customer.paymentTerms.code}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {customer.paymentTerms.dayInterval} days
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-sm">{customer.reason}</p>
                </TableCell>
                <TableCell>
                  {customer.canInvoice ? (
                    <Badge className="bg-green-100 text-green-800">
                      Ready to invoice
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-amber-700">
                      {customer.blockedReason}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleProcessCustomer(customer.customerId)}
                    disabled={!customer.canInvoice || processPending.isPending}
                  >
                    Generate Invoice
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
